import { useCallback } from 'react';
import {
  useAddProductToSessionOrders,
  useRemoveProductToSessionOrders,
} from 'queries';
import { useToastOptions } from '@app/core';
import { PriceProductLine } from 'lib/priceComputation';
import { BillingCardLine } from './BillingCardLine';

export interface CompareProductLine extends PriceProductLine {
  compareToQuantity?: number;
}

interface Props {
  defaultName: string;
  productLine: CompareProductLine;
  sessionId?: string;
}

export const BillingCardProductLine = ({
  defaultName,
  productLine,
  sessionId,
}: Props) => {
  const toastOptions = useToastOptions();

  const { mutateAsync: addProduct } = useAddProductToSessionOrders(
    sessionId,
    toastOptions,
  );
  const { mutateAsync: removeProduct } = useRemoveProductToSessionOrders(
    sessionId,
    toastOptions,
  );

  const product = productLine.product;

  const onRemoveClick = useCallback(() => {
    if (!product?.id) {
      return;
    }
    removeProduct({ id: product.id });
  }, [removeProduct, product]);

  const onAddClick = useCallback(() => {
    if (!product?.id) {
      return;
    }
    addProduct({ id: product.id });
  }, [addProduct, product]);

  return (
    <BillingCardLine
      title={product.title || defaultName}
      unitPrice={productLine.unitPrice}
      quantity={productLine.quantity}
      compareToQuantity={productLine.compareToQuantity}
      total={productLine.quantity * productLine.unitPrice}
      compareToTotal={
        productLine.compareToQuantity
          ? productLine.compareToQuantity * productLine.unitPrice
          : productLine.compareToQuantity
      }
      onRemoveClick={
        productLine.quantity > 0 && !product.pricePerParticipant
          ? onRemoveClick
          : undefined
      }
      onAddClick={
        productLine.quantity === 0 &&
        productLine.compareToQuantity === 1 &&
        !product.pricePerParticipant
          ? onAddClick
          : undefined
      }
    />
  );
};
